<template>
  <v-dialog v-model="dialog" max-width="600">
    <template v-slot:activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        icon="mdi-pencil"
        variant="text"
        size="small"
        color="primary"
      ></v-btn>
    </template>

    <v-card prepend-icon="mdi-account-edit" title="Edit Account">
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="6" lg="6" sm="12">
            <v-text-field
              density="compact"
              placeholder="Fullname*"
              prepend-inner-icon="mdi-account-tie"
              variant="outlined"
              v-model="form.fullname"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" lg="6" sm="12">
            <v-text-field
              density="compact"
              placeholder="Email address*"
              prepend-inner-icon="mdi-email-outline"
              variant="outlined"
              v-model="form.email"
              hint="Email is the login identifier"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              :items="teams"
              label="Team*"
              prepend-inner-icon="mdi-account-multiple"
              variant="outlined"
              v-model="form.team"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              :items="['Team Lead', 'Team Member', 'Super Admin']"
              label="Role*"
              prepend-inner-icon="mdi-application"
              variant="outlined"
              v-model="form.role"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="6">
            <v-switch
              v-model="form.active"
              :label="form.active ? 'Active' : 'Inactive'"
              color="green"
              hide-details
              inset
            ></v-switch>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              density="compact"
              placeholder="New password (optional)"
              prepend-inner-icon="mdi-lock-reset"
              variant="outlined"
              type="password"
              v-model="form.password"
              hint="Leave blank to keep the current password"
              persistent-hint
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-switch
              v-model="form.restrictModules"
              :label="form.restrictModules ? 'Restrict module access' : 'Full access (all modules)'"
              color="primary"
              density="compact"
              hide-details
              inset
            ></v-switch>
            <v-select
              v-if="form.restrictModules"
              v-model="form.modules"
              :items="moduleOptions"
              item-title="title"
              item-value="value"
              label="Allowed modules"
              prepend-inner-icon="mdi-view-grid-outline"
              variant="outlined"
              density="compact"
              multiple
              chips
              closable-chips
              hint="Pick the modules this user can open in the sidebar"
              persistent-hint
              class="mt-2"
            ></v-select>
          </v-col>
        </v-row>

        <v-alert
          v-if="errorMessage"
          type="error"
          density="compact"
          variant="tonal"
          class="mt-2"
        >
          {{ errorMessage }}
        </v-alert>

        <small class="text-caption text-medium-emphasis">*indicates required field</small>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text="Close" variant="plain" @click="dialog = false"></v-btn>
        <v-btn
          color="primary"
          text="Save"
          variant="flat"
          :loading="saving"
          @click="save()"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import constant from '@/constant';
import utils from '@/utils';

export default {
  name: "EditEmployee",
  props: {
    user: {
      type: Object,
      required: true
    },
    teams: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      constant: constant,
      dialog: false,
      saving: false,
      errorMessage: "",
      moduleOptions: [
        { value: 'dashboard', title: 'Home' },
        { value: 'checkin', title: 'Daily Check-In' },
        { value: 'projects', title: 'Projects' },
        { value: 'timeline', title: 'Project Timeline' },
        { value: 'sprints', title: 'Sprints' },
        { value: 'board', title: 'Board' },
        { value: 'tickets', title: 'All Project Tickets' },
        { value: 'it', title: 'IT Support' },
        { value: 'qa', title: 'QA / Test Cases' },
        { value: 'teams', title: 'Teams' },
        { value: 'users', title: 'User Management' },
      ],
      form: {
        fullname: "",
        email: "",
        team: "",
        role: "",
        active: true,
        password: "",
        restrictModules: false,
        modules: []
      }
    };
  },
  watch: {
    dialog(open) {
      if (open) {
        this.errorMessage = "";
        const mods = Array.isArray(this.user.modules) ? this.user.modules : [];
        this.form = {
          fullname: this.user.fullname || "",
          email: this.user.email || "",
          team: this.user.team || "",
          role: this.user.role || "",
          active: this.user.active === 1 || this.user.active === true,
          password: "",
          restrictModules: mods.length > 0,
          modules: [...mods]
        };
      }
    }
  },
  methods: {
    save() {
      this.saving = true;
      this.errorMessage = "";

      const f = new FormData();
      f.append("id", this.user.id);
      f.append("fullname", this.form.fullname);
      f.append("email", this.form.email);
      f.append("team", this.form.team);
      f.append("role", this.form.role);
      f.append("active", this.form.active);
      if (this.form.password) {
        f.append("password", this.form.password);
      }
      // Module access: send the allowed keys, or an empty list for full access.
      f.append("modules", JSON.stringify(this.form.restrictModules ? this.form.modules : []));

      axios.post(utils._api(this.constant.update_user_details), f, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      }).then((response) => {
        this.saving = false;
        this.dialog = false;
        this.$emit('toggle-message', response.data.message);
      }).catch((error) => {
        this.saving = false;
        const data = error.response && error.response.data;
        if (data && data.errors) {
          this.errorMessage = Object.values(data.errors).flat().join(' ');
        } else {
          this.errorMessage = (data && data.message) || "Update failed.";
        }
      });
    }
  }
};
</script>
